import React from 'react'
import { GlobalContext } from '../App'
import GlobalState from '../Interfaces/IGlobalState'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBars, faChevronDown, faGear, faPlus, faSignOut, faXmark } from '@fortawesome/free-solid-svg-icons'
import { MenuItems } from './MenuItem'
import { AppPermission, hasPermission } from '../Permissions/PermissionDefinitions'

const MenuBar: React.FC = () => {
    const { globalVariable, setGlobalVariable } = React.useContext(GlobalContext)
    const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false)
    const [isSettingsOpen, setIsSettingsOpen] = React.useState(false)
    const settingsItems = MenuItems.filter(item => ['audit-log', 'duplix', 'user-accounts'].includes(item.id))
    const visibleSettingsItems = settingsItems.filter(item => item.permissions.some(permission => globalVariable.permissions?.includes(permission)))
    const visiblePrimaryItems = MenuItems.filter(item => !item.hidden && (!globalVariable.sessionValid || !item.permissions?.length || item.permissions.some(permission => globalVariable.permissions?.includes(permission))))
    const settingsSelected = settingsItems.some(item => item.id === globalVariable.menuSelection)
    const displayName = [globalVariable.firstName, globalVariable.lastName].filter(Boolean).join(' ') || globalVariable.username

    React.useEffect(() => {
        if (!isMobileMenuOpen) return

        const previousOverflow = document.body.style.overflow
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsMobileMenuOpen(false)
                setIsSettingsOpen(false)
            }
        }

        document.body.style.overflow = 'hidden'
        document.addEventListener('keydown', closeOnEscape)

        return () => {
            document.body.style.overflow = previousOverflow
            document.removeEventListener('keydown', closeOnEscape)
        }
    }, [isMobileMenuOpen])

    const updateMenuSelection = (selection: string) => {
        setGlobalVariable((prev: GlobalState) => ({
            ...prev,
            menuSelection: selection
        }))
        setIsMobileMenuOpen(false)
        setIsSettingsOpen(false)
    }

    const toggleMobileMenu = () => setIsMobileMenuOpen(value => !value)

    const logout = () => {
        localStorage.clear()
        window.document.location.reload()
    }

    return (
        <header className="fixed left-0 right-0 top-0 z-50 bg-gradient-to-r from-blue-500 to-purple-600 font-roboto text-white shadow-lg">
            <nav className="container mx-auto px-3 sm:px-4" aria-label="Päänavigointi">
                <div className="flex h-16 items-center justify-between md:hidden">
                    <button
                        type="button"
                        onClick={toggleMobileMenu}
                        className="flex h-11 w-11 items-center justify-center rounded-xl text-white transition hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-blue-500"
                        aria-label={isMobileMenuOpen ? 'Sulje valikko' : 'Avaa valikko'}
                        aria-controls="mobile-navigation"
                        aria-expanded={isMobileMenuOpen}
                    >
                        <FontAwesomeIcon icon={isMobileMenuOpen ? faXmark : faBars} className="text-xl" />
                    </button>
                    <button type="button" onClick={() => updateMenuSelection('home')} className="flex items-center gap-2 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-white" aria-label="Hexacat-etusivu">
                        <img src="/hexacat.svg" alt="" className="h-9 w-9 object-contain brightness-0 invert" />
                        <span className="text-xl font-bold">Hexacat</span>
                    </button>
                    {hasPermission(globalVariable.permissions, AppPermission.ADDRESS_CREATE) ? (
                        <button type="button" onClick={() => updateMenuSelection('add-customer')} className="flex h-11 w-11 items-center justify-center rounded-xl text-white transition hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-purple-600" aria-label="Uusi kohde">
                            <FontAwesomeIcon icon={faPlus} className="text-xl" />
                        </button>
                    ) : <span className="h-11 w-11" aria-hidden="true" />}
                </div>

                {isMobileMenuOpen && (
                    <div id="mobile-navigation" className="fixed inset-x-0 bottom-0 top-16 overflow-y-auto bg-slate-100 text-slate-900 md:hidden">
                        <div className="mx-auto flex min-h-full w-full max-w-lg flex-col p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
                            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                                <div className="border-b border-slate-100 px-4 py-3">
                                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Valikko</p>
                                    {displayName && <p className="mt-0.5 truncate text-sm font-medium text-slate-700">{displayName}</p>}
                                </div>
                                <ul className="space-y-1 p-2">
                                    {visiblePrimaryItems.map(item => {
                                        const isSelected = globalVariable.menuSelection === item.id
                                        return (
                                            <li key={item.id}>
                                                <button
                                                    type="button"
                                                    onClick={() => updateMenuSelection(item.id)}
                                                    className={`group flex min-h-12 w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-base font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${isSelected ? 'bg-blue-50 text-blue-700' : 'text-slate-700 hover:bg-slate-50 hover:text-slate-950'}`}
                                                    aria-current={isSelected ? 'page' : undefined}
                                                >
                                                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition ${isSelected ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500 group-hover:bg-slate-200'}`}>
                                                        <FontAwesomeIcon icon={item.icon} className="text-sm" />
                                                    </span>
                                                    <span>{item.title}</span>
                                                </button>
                                            </li>
                                        )
                                    })}
                                    {visibleSettingsItems.length > 0 && (
                                        <li>
                                            <button
                                                type="button"
                                                onClick={() => setIsSettingsOpen(value => !value)}
                                                className={`group flex min-h-12 w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-base font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${settingsSelected ? 'bg-blue-50 text-blue-700' : 'text-slate-700 hover:bg-slate-50 hover:text-slate-950'}`}
                                                aria-expanded={isSettingsOpen}
                                                aria-controls="mobile-settings-navigation"
                                            >
                                                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition ${settingsSelected ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500 group-hover:bg-slate-200'}`}>
                                                    <FontAwesomeIcon icon={faGear} className="text-sm" />
                                                </span>
                                                <span>Asetukset</span>
                                                <FontAwesomeIcon icon={faChevronDown} className={`ml-auto mr-1 text-xs text-slate-400 transition-transform ${isSettingsOpen ? 'rotate-180' : ''}`} />
                                            </button>
                                            {isSettingsOpen && (
                                                <ul id="mobile-settings-navigation" className="ml-7 mt-1 space-y-1 border-l-2 border-blue-100 pb-1 pl-4">
                                                    {visibleSettingsItems.map(item => {
                                                        const isSelected = globalVariable.menuSelection === item.id
                                                        return (
                                                            <li key={item.id}>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => updateMenuSelection(item.id)}
                                                                    className={`flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${isSelected ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                                                    aria-current={isSelected ? 'page' : undefined}
                                                                >
                                                                    <FontAwesomeIcon icon={item.icon} className="w-4 text-slate-400" />
                                                                    <span>{item.title}</span>
                                                                </button>
                                                            </li>
                                                        )
                                                    })}
                                                </ul>
                                            )}
                                        </li>
                                    )}
                                </ul>
                            </div>

                            <button type="button" onClick={logout} className="mt-auto flex min-h-12 w-full items-center gap-3 rounded-xl px-4 py-3 text-left font-semibold text-red-600 transition hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500">
                                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-50 text-red-500">
                                    <FontAwesomeIcon icon={faSignOut} className="text-sm" />
                                </span>
                                <span>Kirjaudu ulos</span>
                            </button>
                        </div>
                    </div>
                )}

                <ul className="hidden w-full flex-wrap items-center gap-1 py-2 md:flex">
                    {visiblePrimaryItems.map(item => {
                        const isSelected = globalVariable.menuSelection === item.id
                        return (
                            <li key={item.id} className="text-left">
                                <button type="button" onClick={() => updateMenuSelection(item.id)} className={`flex items-center rounded-xl px-4 py-2 text-left transition duration-200 hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${isSelected ? 'bg-white/15' : ''}`} aria-current={isSelected ? 'page' : undefined}>
                                    <FontAwesomeIcon icon={item.icon} className="mr-2" />
                                    <span className="font-semibold">{item.title}</span>
                                </button>
                            </li>
                        )
                    })}
                    {visibleSettingsItems.length > 0 && (
                        <li className="relative text-left">
                            <button type="button" onClick={() => setIsSettingsOpen(value => !value)} className={`flex items-center rounded-xl px-4 py-2 text-left transition duration-200 hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${settingsSelected ? 'bg-white/15' : ''}`} aria-expanded={isSettingsOpen}>
                                <FontAwesomeIcon icon={faGear} className="mr-2" />
                                <span className="font-semibold">Asetukset</span>
                                <FontAwesomeIcon icon={faChevronDown} className={`ml-2 text-xs transition ${isSettingsOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {isSettingsOpen && (
                                <ul className="absolute left-0 top-full z-50 mt-1 min-w-52 overflow-hidden rounded-xl bg-blue-700/95 py-1 shadow-lg">
                                    {visibleSettingsItems.map(item => (
                                        <li key={item.id}>
                                            <button type="button" onClick={() => updateMenuSelection(item.id)} className={`flex min-h-11 w-full items-center px-4 text-left hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white ${globalVariable.menuSelection === item.id ? 'bg-white/15' : ''}`}>
                                                <FontAwesomeIcon icon={item.icon} className="mr-2 w-4" />
                                                <span className="font-semibold">{item.title}</span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </li>
                    )}
                    <li className="text-left">
                        <button type="button" onClick={logout} className="flex items-center rounded-xl px-4 py-2 text-left transition duration-200 hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
                            <FontAwesomeIcon icon={faSignOut} className="mr-2" />
                            <span className="font-semibold">Kirjaudu ulos</span>
                        </button>
                    </li>
                </ul>
            </nav>
        </header>
    )
}

export default MenuBar
